/**
 * Extract parameters from prompt text (e.g., {warehouse}, {customer})
 * @param text The prompt text containing parameters
 * @returns Array of parameter names without braces
 */
export function extractParameters(text: string): string[] {
  const regex = /\{([^}]+)\}/g;
  const params: string[] = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    const param = match[1].trim();
    if (param && !params.includes(param)) {
      params.push(param);
    }
  }

  return params;
}

/**
 * Validate parameter syntax in prompt text
 * @param text The prompt text to validate
 * @returns Error message if invalid, null if valid
 */
export function validateParameterSyntax(text: string): string | null {
  // Check for balanced curly braces
  const openCount = (text.match(/\{/g) || []).length;
  const closeCount = (text.match(/\}/g) || []).length;

  if (openCount !== closeCount) {
    return 'Unbalanced curly braces: parameters must have matching { and }';
  }

  // Check for empty parameters {}
  if (text.includes('{}')) {
    return 'Empty parameter {} found: parameters must have a name';
  }

  // Check for nested braces
  let stack = 0;
  for (const char of text) {
    if (char === '{') {
      stack++;
      if (stack > 1) {
        return 'Nested parameters are not allowed';
      }
    } else if (char === '}') {
      stack--;
      if (stack < 0) {
        return 'Closing brace } without opening brace {';
      }
    }
  }

  return null;
}

/**
 * Format parameter name for display (capitalize first letter)
 * @param param Parameter name (e.g., "warehouse")
 * @returns Formatted name (e.g., "Warehouse")
 */
export function formatParameterName(param: string): string {
  return param.charAt(0).toUpperCase() + param.slice(1);
}
